// Flow rename validation + decision logic. Kept pure (no React/store/network) so the
// gating rules can be unit-tested in isolation and shared across rename surfaces.

export const FLOW_NAME_MIN_LENGTH = 3
// Server caps the name at 90 chars (config_service rename_flow). Keep the client in sync
// so a too-long name fails fast inline instead of round-tripping into a 400.
export const FLOW_NAME_MAX_LENGTH = 90

export interface FlowNameValidation {
  valid: boolean
  error?: string
}

export const validateFlowName = (name: string): FlowNameValidation => {
  const trimmed = name.trim()
  if (trimmed.length === 0) return { valid: false, error: 'Agent name cannot be empty.' }
  if (trimmed.length < FLOW_NAME_MIN_LENGTH)
    return {
      valid: false,
      error: `Agent name must be at least ${FLOW_NAME_MIN_LENGTH} characters long.`,
    }
  if (trimmed.length > FLOW_NAME_MAX_LENGTH)
    return { valid: false, error: `Agent name cannot exceed ${FLOW_NAME_MAX_LENGTH} characters.` }
  return { valid: true }
}

export type RenameAction =
  | { type: 'noop' }
  | { type: 'invalid'; error: string }
  | { type: 'store'; name: string }
  | { type: 'save'; name: string }

export interface ResolveRenameArgs {
  value: string
  currentName: string
  // True only once the flow exists in the DB (flowData.phlo.id present). An unsaved flow
  // has no row to PATCH, so its rename is held in the store until the first save/publish.
  isPersisted: boolean
}

/**
 * Decide what a rename commit should do:
 * - `noop`    — value unchanged
 * - `invalid` — fails validation; caller reverts and surfaces the error
 * - `store`   — valid change on an unsaved flow; keep in store only (no API)
 * - `save`    — valid change on a persisted flow; call the rename API
 */
export const resolveRenameAction = ({
  value,
  currentName,
  isPersisted,
}: ResolveRenameArgs): RenameAction => {
  const trimmed = value.trim()
  if (trimmed === currentName.trim()) return { type: 'noop' }

  const { valid, error } = validateFlowName(trimmed)
  if (!valid) return { type: 'invalid', error: error ?? 'Invalid agent name.' }

  return isPersisted ? { type: 'save', name: trimmed } : { type: 'store', name: trimmed }
}

/**
 * A Vibe Agent name suggestion (FLOW_NAME_SUGGESTED) should only be applied when the user
 * hasn't manually named the flow this session — otherwise the suggestion would silently
 * revert a deliberate rename.
 */
export const shouldApplyVibeSuggestedName = (
  suggestedName: string | null | undefined,
  flowNameUserEdited: boolean,
): suggestedName is string => Boolean(suggestedName) && !flowNameUserEdited
